/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { updateUserPreferences } from '../api/auth';

export type ThemePreference = 'light' | 'dark';

type ThemePreferenceState = {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference, options?: { persist?: boolean }) => Promise<void>;
  applyUserTheme: (theme: ThemePreference | null | undefined) => void;
};

export const ThemePreferenceContext = createContext<ThemePreferenceState | undefined>(undefined);

const COOP_THEME_KEY = 'coop_theme_preference';
const FLOWBITE_THEME_KEY = 'flowbite-theme-mode';
const FLOWBITE_SYNC_EVENT = 'flowbite-theme-mode-sync';

export const normalizeThemePreference = (value: unknown): ThemePreference | null => {
  if (value === 'light' || value === 'dark') return value;
  return null;
};

const readStorageValue = (key: string) => {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const readInitialThemePreference = (): ThemePreference =>
  normalizeThemePreference(readStorageValue(COOP_THEME_KEY)) ??
  normalizeThemePreference(readStorageValue(FLOWBITE_THEME_KEY)) ??
  'light';

const writeThemeStorage = (theme: ThemePreference) => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(COOP_THEME_KEY, theme);
    localStorage.setItem(FLOWBITE_THEME_KEY, theme);
  } catch {
    // ignore
  }
};

const hasStoredToken = () => {
  try {
    return Boolean(typeof localStorage !== 'undefined' && localStorage.getItem('coop_token'));
  } catch {
    return false;
  }
};

export const applyThemePreference = (theme: ThemePreference) => {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.style.colorScheme = theme;
  }
  writeThemeStorage(theme);
  try {
    document.dispatchEvent(new CustomEvent(FLOWBITE_SYNC_EVENT, { detail: theme }));
  } catch {
    // ignore
  }
};

export const initializeThemePreference = () => {
  applyThemePreference(readInitialThemePreference());
};

export function ThemePreferenceProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(() => readInitialThemePreference());

  useEffect(() => {
    // Legacy desktop controls publish this event. Keep mobile preferences in
    // sync without importing Flowbite into the merchant header or re-emitting it.
    const sync = (value: unknown) => {
      const next = normalizeThemePreference(value);
      if (!next) return;
      setThemeState(next);
      writeThemeStorage(next);
      document.documentElement.classList.toggle('dark', next === 'dark');
      document.documentElement.style.colorScheme = next;
    };
    const onMode = (event: Event) => sync((event as CustomEvent<unknown>).detail);
    const onStorage = (event: StorageEvent) => {
      if (event.key === COOP_THEME_KEY || event.key === FLOWBITE_THEME_KEY) sync(event.newValue);
    };
    document.addEventListener(FLOWBITE_SYNC_EVENT, onMode);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener(FLOWBITE_SYNC_EVENT, onMode);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setTheme = useCallback(async (nextTheme: ThemePreference, options?: { persist?: boolean }) => {
    setThemeState(nextTheme);
    applyThemePreference(nextTheme);
    if (options?.persist === false) return;
    if (!hasStoredToken()) return;
    try {
      await updateUserPreferences({ preferredTheme: nextTheme });
    } catch {
      // Local preference remains useful for login/anonymous states and offline transitions.
    }
  }, []);

  const applyUserTheme = useCallback((nextTheme: ThemePreference | null | undefined) => {
    const normalized = normalizeThemePreference(nextTheme);
    if (!normalized) return;
    setThemeState(normalized);
    applyThemePreference(normalized);
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      applyUserTheme,
    }),
    [theme, setTheme, applyUserTheme],
  );

  return <ThemePreferenceContext.Provider value={value}>{children}</ThemePreferenceContext.Provider>;
}
